import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Heart, Users, Printer, ChevronRight, Menu, X, Mail, Copy, Check } from "lucide-react";
import { ThemeToggle } from "../components/ThemeToggle";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useTranslation } from "../i18n/useTranslation";

const SUPPORT_EMAIL = "support@prayergroup.site";

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
    } catch {
      // Fallback for older browsers / insecure contexts
      const ta = document.createElement("textarea");
      ta.value = SUPPORT_EMAIL;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const benefits = [
    {
      icon: Heart,
      title: "Track Your Prayers",
      description: "Record prayer requests, add notes, and celebrate when prayers are answered. Keep a meaningful journal of your spiritual journey."
    },
    {
      icon: Users,
      title: "Pray Together",
      description: "Create or join prayer groups. Share requests with your community and support each other through life's moments."
    },
    {
      icon: Printer,
      title: "Print & Reflect",
      description: "Print beautiful lists of your prayers. Perfect for offline reflection, journaling, or sharing with those not online."
    }
  ];

  return (
    <div className="min-h-screen bg-stone-light noise-bg">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="glass rounded-full px-6 py-3 nav-shadow flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <div className="w-8 h-8 rounded-full bg-sage flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span className="font-serif font-semibold text-lg text-stone-dark">Prayer Tracker</span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <Link 
                to="/" 
                className="text-stone-dark/80 hover:text-sage transition-colors font-medium"
                data-testid="nav-home"
              >
                Home
              </Link>
              <a 
                href="#about" 
                className="text-stone-dark/80 hover:text-sage transition-colors font-medium"
                data-testid="nav-about"
              >
                About
              </a>
              <Link 
                to="/how-it-works" 
                className="text-stone-dark/80 hover:text-sage transition-colors font-medium"
                data-testid="nav-how-it-works"
              >
                How It Works
              </Link>
              <Link 
                to="/donate" 
                className="text-stone-dark/80 hover:text-sage transition-colors font-medium"
                data-testid="nav-donate"
              >
                Donate
              </Link>
              <Link to="/login" data-testid="nav-login">
                <Button 
                  variant="ghost" 
                  className="text-sage hover:bg-sage/10 font-medium"
                >
                  Log In
                </Button>
              </Link>
              <Link to="/signup" data-testid="nav-signup">
                <Button className="bg-sage hover:bg-sage-dark text-white rounded-full px-6 btn-glow">
                  Get Started
                </Button>
              </Link>
              <ThemeToggle />
              <LanguageSwitcher />
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 hover:bg-stone/50 rounded-full transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5 text-stone-dark" />
              ) : (
                <Menu className="w-5 h-5 text-stone-dark" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden glass rounded-2xl mt-2 p-4 nav-shadow animate-fade-in">
              <div className="flex flex-col gap-2">
                <Link 
                  to="/" 
                  className="px-4 py-2 text-stone-dark/80 hover:bg-stone/50 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <a 
                  href="#about" 
                  className="px-4 py-2 text-stone-dark/80 hover:bg-stone/50 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About
                </a>
                <Link 
                  to="/how-it-works" 
                  className="px-4 py-2 text-stone-dark/80 hover:bg-stone/50 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  How It Works
                </Link>
                <Link 
                  to="/donate" 
                  className="px-4 py-2 text-stone-dark/80 hover:bg-stone/50 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Donate
                </Link>
                <Link 
                  to="/login" 
                  className="px-4 py-2 text-sage hover:bg-sage/10 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Log In
                </Link>
                <Link 
                  to="/signup"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button className="w-full bg-sage hover:bg-sage-dark text-white rounded-full mt-2">
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 md:pt-40 md:pb-32">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Text Content */}
            <div className="text-left">
              <div className="inline-flex items-center gap-2 bg-gold/10 text-gold-dark px-4 py-2 rounded-full mb-6 animate-fade-in">
                <Heart className="w-4 h-4" />
                <span className="text-sm font-medium">A peaceful place for your prayers</span>
              </div>
              
              <h1 
                className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-stone-dark leading-tight mb-6 animate-fade-in stagger-1"
                data-testid="hero-headline"
              >
                Keep Your Prayers{" "}
                <span className="text-sage">Close to Heart</span>
              </h1>
              
              <p 
                className="text-lg text-stone-dark/70 mb-8 max-w-lg animate-fade-in stagger-2"
                data-testid="hero-subheadline"
              >
                A simple, warm space to record your prayers, track answered requests, and share your spiritual journey with those who matter most.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in stagger-3">
                <Link to="/signup" data-testid="hero-cta-primary">
                  <Button 
                    size="lg" 
                    className="bg-sage hover:bg-sage-dark text-white rounded-full px-8 py-6 text-base font-medium btn-glow w-full sm:w-auto"
                  >
                    Start Your Journey
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/how-it-works" data-testid="hero-cta-secondary">
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="border-2 border-sage text-sage hover:bg-sage hover:text-white rounded-full px-8 py-6 text-base font-medium w-full sm:w-auto transition-all"
                  >
                    See How It Works
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="relative animate-fade-in stagger-4">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1739081711376-f52158938992?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxwZWFjZWZ1bCUyMG5hdHVyZSUyMHN1bnJpc2UlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc3MjMxMDcwOHww&ixlib=rb-4.1.0&q=85"
                  alt="Peaceful sunrise over mountains"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-dark/20 to-transparent" />
              </div>
              
              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 -left-6 glass rounded-2xl p-4 nav-shadow hidden lg:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-gold-dark" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-dark/60">Prayers Recorded</p>
                    <p className="font-serif font-semibold text-xl text-stone-dark">10,000+</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="about" className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="font-serif text-3xl sm:text-4xl font-semibold text-stone-dark mb-4"
              data-testid="benefits-title"
            >
              Everything You Need for Your Prayer Life
            </h2>
            <p className="text-stone-dark/70 max-w-2xl mx-auto">
              Whether you pray alone or with others, Prayer Tracker helps you stay organized, connected, and grateful.
            </p>
          </div>

          {/* Benefit Cards - Tetris/Bento Grid */}
          <div className="grid md:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={benefit.title}
                className={`bg-stone-light rounded-2xl p-8 card-hover border border-stone ${
                  index === 1 ? 'md:translate-y-4' : ''
                }`}
                data-testid={`benefit-card-${index + 1}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-sage/10 flex items-center justify-center mb-6">
                  <benefit.icon className="w-7 h-7 text-sage" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-stone-dark mb-3">
                  {benefit.title}
                </h3>
                <p className="text-stone-dark/70 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-sage rounded-3xl p-12 md:p-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-gold rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
            </div>
            
            <div className="relative z-10">
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-white mb-4">
                Ready to Start Your Prayer Journey?
              </h2>
              <p className="text-white/80 mb-8 max-w-xl mx-auto">
                Join thousands of others who have found peace and organization in their prayer life.
              </p>
              <Link to="/signup" data-testid="cta-signup">
                <Button 
                  size="lg" 
                  className="bg-white text-sage hover:bg-stone-light rounded-full px-8 py-6 text-base font-medium"
                >
                  Create Free Account
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-20 px-4 bg-white" data-testid="contact-section">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-sage/10 text-sage px-4 py-2 rounded-full mb-5">
            <Mail className="w-4 h-4" />
            <span className="text-sm font-medium">{t("contact.weAreHereToHelp")}</span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-stone-dark mb-4" data-testid="contact-headline">
            {t("contact.contactUs")}
          </h2>
          <p className="text-base md:text-lg text-stone-dark/70 mb-8 max-w-xl mx-auto">
            {t("contact.contactBlurb")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-white rounded-full px-8 py-4 text-base font-medium btn-glow transition-colors"
              data-testid="contact-email-link"
            >
              <Mail className="w-5 h-5" />
              {SUPPORT_EMAIL}
            </a>
            <button
              type="button"
              onClick={copyEmail}
              aria-live="polite"
              className="inline-flex items-center gap-2 border border-sage/40 text-sage hover:bg-sage/10 rounded-full px-5 py-4 text-sm font-medium transition-colors"
              data-testid="contact-email-copy"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? t("help.copied") : t("help.copy")}
            </button>
          </div>
          <p className="text-sm text-stone-dark/50 mt-5">
            {t("contact.mailtoHint")}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-stone">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-sage flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span className="font-serif font-semibold text-stone-dark">Prayer Tracker</span>
            </div>
            <p className="text-stone-dark/60 text-sm">
              © 2026 Prayer Tracker. Made with love for those who pray.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
